// API-функции Stage 6: акты и финансовое закрытие заявки
// (REGWAY_CRM_v8_Stage6_Financial_Closure_Contract.md §8).
// Вынесены в отдельный модуль: endpoints.ts периодически синхронизируется
// внешним процессом (см. маркер __SYNC_PROBE__) — правки в нём теряются,
// поэтому новые вызовы живут здесь (как finance-bank.ts для Stage 5).
// Все команды — apiCommand с обязательным Idempotency-Key (генерируется
// автоматически, если ключ не передан) + revision — по контракту §8.

import { apiCommand, apiGet, apiList } from './client';
import type {
    Act, ActLine, ClosureChecklist, ClosurePolicy, FinancialDelivery,
    InvoiceLine, RequestClosure, RequestClosureSummary,
} from './types';

// ---------- Чтение (read-only viewset'ы) ----------

/** Список актов; фильтры — точные поля модели (issuer, payer, status, ...). */
export function listActs(params?: Record<string, unknown>) {
  return apiList<Act>('/finance/act/', params);
}

export function getAct(id: string) {
  return apiGet<Act>(`/finance/act/${id}/`);
}

/** Строки актов; фильтры: act, invoice_line, item. */
export function listActLines(params?: Record<string, unknown>) {
  return apiList<ActLine>('/finance/act-line/', params);
}

/** Строки счетов; фильтры: invoice, item. */
export function listInvoiceLines(params?: Record<string, unknown>) {
  return apiList<InvoiceLine>('/finance/invoice-line/', params);
}

/** Записи о доставке/подписи; фильтры: act, invoice. */
export function listFinancialDeliveries(params?: Record<string, unknown>) {
  return apiList<FinancialDelivery>('/finance/financial-delivery/', params);
}

// ---------- Команды актов (§1, роли accountant/supervisor/admin) ----------

export interface ActDraftLinePayload {
  invoice_line_id: string;
  amount: string;
}

export interface ActDraftPayload {
  issuer_id: string;
  payer_id: string;
  number: string;
  /** Дата акта (YYYY-MM-DD). */
  date: string;
  currency: string;
  lines: ActDraftLinePayload[];
  file_version_id?: string;
}

/** Черновик акта со строками — POST /finance/act/draft/. */
export function createActDraft(body: ActDraftPayload) {
  return apiCommand<Act & { act_id?: string }>('/finance/act/draft/', body);
}

/** Полная замена строк черновика — POST /finance/act/{id}/set-lines/. */
export function setActLines(id: string, body: { revision?: number; lines: ActDraftLinePayload[] }) {
  return apiCommand<Act>(`/finance/act/${id}/set-lines/`, body);
}

/** Подтверждение акта draft → issued — POST /finance/act/{id}/confirm/. */
export function confirmAct(id: string, revision?: number) {
  return apiCommand<Act>(`/finance/act/${id}/confirm/`, { revision });
}

/** Отмена акта (draft|issued → cancelled); reason обязателен. */
export function cancelAct(id: string, body: { revision?: number; reason: string }) {
  return apiCommand<Act>(`/finance/act/${id}/cancel/`, body);
}

// ---------- FinancialDelivery — подпись и оригинал (§4) ----------

/** Создать/получить запись доставки по цели (XOR act_id | invoice_id),
    идемпотентно по цели — POST /finance/financial-delivery/draft/. */
export function createDeliveryDraft(body: { act_id?: string; invoice_id?: string }) {
  return apiCommand<FinancialDelivery & { delivery_id?: string }>(
    '/finance/financial-delivery/draft/', body);
}

/** Ручная фиксация статуса подписи/оригинала — basis обязателен;
    ответ включает status_source='manual'. */
export function markDelivery(id: string, body: {
  revision?: number;
  edo_status?: string;
  original_status?: string;
  original_required?: boolean;
  basis: string;
  file_version_id?: string;
}) {
  return apiCommand<FinancialDelivery>(`/finance/financial-delivery/${id}/mark/`, body);
}

/** Освобождение от обязательного оригинала (Fix1 F1-02): ТОЛЬКО
    supervisor/admin, basis обязателен; повтор идемпотентен —
    POST /finance/financial-delivery/{id}/exempt-original/. */
export function exemptDeliveryOriginal(id: string, body: {
  revision?: number;
  basis: string;
}) {
  return apiCommand<FinancialDelivery>(
    `/finance/financial-delivery/${id}/exempt-original/`, body);
}

// ---------- Политика закрытия (§5) ----------

export function listClosurePolicies(params?: Record<string, unknown>) {
  return apiList<ClosurePolicy>('/finance/closure-policy/', params);
}

/** Новая версия политики (supervisor/admin): deactivates старые версии. */
export function setClosurePolicy(body: {
  require_result: boolean;
  require_acts: boolean;
  require_signature: boolean;
  require_original: boolean;
  note?: string;
}) {
  return apiCommand<ClosurePolicy>('/finance/closure-policy/set/', body);
}

// ---------- Чек-лист / закрытие / повторное открытие (§6–§7) ----------

/** Единый чек-лист закрытия заявки (§6). */
export function getClosureChecklist(requestId: string) {
  return apiGet<ClosureChecklist>('/finance/request-closure/checklist/', { request_id: requestId });
}

/** Finance-агрегаты + список актов заявки для UI (§8). */
export function getClosureSummary(requestId: string) {
  return apiGet<RequestClosureSummary>('/finance/request-closure/summary/', { request_id: requestId });
}

/** История закрытий/переоткрытий заявки (§7). */
export function getClosureHistory(requestId: string) {
  return apiList<RequestClosure>('/finance/request-closure/history/', { request_id: requestId });
}

/** Закрытие заявки (accountant/supervisor/admin): блокеры → 409 со списком кодов. */
export function closeRequest(requestId: string, revision?: number) {
  return apiCommand<{ request_id?: string; stage?: string; closure_id?: string }>(
    '/finance/request-closure/close/', { request_id: requestId, revision });
}

/** Повторное открытие (ТОЛЬКО supervisor/admin); reason обязателен. */
export function reopenRequest(requestId: string, body: { revision?: number; reason: string }) {
  return apiCommand<{ request_id?: string; stage?: string }>(
    '/finance/request-closure/reopen/', { request_id: requestId, ...body });
}
